/**
 Project	ENT - Environnement numérique de travail

 @file 	    keycloak.service.ts
 @author  	Cortes Zuka Luan <luan.cortes@ictvs.ch>
 @copyright © 2023, ICT-vs. All rights reserved.
**/

import { Injectable } from '@angular/core';
import Keycloak, { KeycloakInitOptions, KeycloakProfile } from 'keycloak-js';
import { environment } from '../../../environments/environment';
import { Observable, Subscription, from } from 'rxjs';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  public keycloak: Keycloak;
  isMaintenance = false;
  private subscription: Subscription = new Subscription();

  constructor(private _routeStatusService: RouteStatusService) {}

  async init(options?: KeycloakInitOptions): Promise<boolean> {
    this.keycloak = new Keycloak(environment.keycloakConfig);
    return this.keycloak.init(options);
  }

  async refreshToken(): Promise<boolean> {
    try {
      const refreshed = await this.keycloak.updateToken(5); // Rafraîchir si le token expire dans les 5 secondes
      return refreshed;
    } catch {
      return false;
    }
  }

  isAuthenticated(): boolean {
    //Set up token for maintenance
    const urls = ['/*'];
    this.subscription.add(
      this._routeStatusService
        .getRouteStatusForUrls(urls)
        .subscribe(statuses => {
          // Retrieve the status for full maintenance
          this.isMaintenance = statuses['/*'] ?? false;
          if (this.isMaintenance) {
            if (this.keycloak.authenticated) {
              // Remove unused token properties in maintenance mode
              delete this.keycloak.tokenParsed['aud'];
              delete this.keycloak.tokenParsed['acr'];
              delete this.keycloak.tokenParsed['azp'];
              delete this.keycloak.tokenParsed['auth_time'];
              delete this.keycloak.tokenParsed['email_verified'];
              delete this.keycloak.tokenParsed['exp'];
              delete this.keycloak.tokenParsed['iat'];
              delete this.keycloak.tokenParsed['iss'];
              delete this.keycloak.tokenParsed['jti'];
              delete this.keycloak.tokenParsed['nonce'];
              delete this.keycloak.tokenParsed['session_state'];
              delete this.keycloak.tokenParsed['sid'];
              delete this.keycloak.tokenParsed['sub'];
              delete this.keycloak.tokenParsed['typ'];
              delete this.keycloak.tokenParsed['realm_access'];
              delete this.keycloak.tokenParsed['resource_access'];
              delete this.keycloak.tokenParsed['scope'];
            }
          }
        })
    );
    return this.keycloak.authenticated;
  }

  login(): void {
    this.keycloak.login();
  }

  logout(redirectUri?: string): void {
    this.keycloak.logout({ redirectUri });
  }

  loadUserProfile(): Observable<KeycloakProfile> {
    return from(this.keycloak.loadUserProfile());
  }

  clearToken(): void {
    this.keycloak.clearToken();
  }

  isTokenExpired(): boolean {
    return this.keycloak.isTokenExpired();
  }
}
